import type { UppFloorPlanTheme } from '../plan/floor-plan-theme';
import { registerFloorPlanShapePainter } from './paint';

const DOOR_WALL_CM = 10;
const DOOR_LEAF_THICK_CM = 4;

/** v3 `_drawLeftDoor` geometry in local px (origin top-left of wall strip; hinge at (0, H)). */
function addDoorToLeftGeometry(
  K: any,
  parent: any,
  openW: number,
  theme: UppFloorPlanTheme,
  scalePxPerCm: number,
): void {
  const H = Math.max(3, DOOR_WALL_CM * scalePxPerCm + 2);
  const doorTh = Math.max(1.5, DOOR_LEAF_THICK_CM * scalePxPerCm);
  const strokeWall = theme.elementStroke;
  const strokeW = Math.max(1, doorTh);
  const thinLine = theme.elementStrokeWidth;

  // Plan-view wall opening band (lintel / head of the frame): filled like the room between the
  // two jambs. Matches v3 `_drawLeftDoor` wall strip above the hinge line — not a stray bbox.
  parent.add(
    new K.Rect({
      x: 0,
      y: 0,
      width: openW,
      height: H,
      fill: theme.roomFill,
      listening: false,
    }),
  );

  parent.add(
    new K.Line({
      points: [0, 0, 0, H],
      stroke: strokeWall,
      strokeWidth: strokeW,
      lineCap: 'butt',
      listening: false,
    }),
  );
  parent.add(
    new K.Line({
      points: [openW, 0, openW, H],
      stroke: strokeWall,
      strokeWidth: strokeW,
      lineCap: 'butt',
      listening: false,
    }),
  );

  parent.add(
    new K.Shape({
      stroke: strokeWall,
      strokeWidth: thinLine,
      listening: false,
      sceneFunc(context: any, shape: any) {
        context.beginPath();
        // Match p5 `arc(x, y+h, w*2, w*2, 0, HALF_PI)`: sweep from east → south (door swing into +y).
        context.arc(0, H, openW, 0, Math.PI / 2, false);
        context.strokeShape(shape);
      },
    }),
  );

  parent.add(
    new K.Rect({
      x: 0,
      y: H,
      width: doorTh,
      height: openW,
      fill: theme.roomFill,
      stroke: strokeWall,
      strokeWidth: thinLine,
      listening: false,
    }),
  );
}

registerFloorPlanShapePainter('door:TOLEFT', (ctx) => {
  const { K, group, widthPx: openW, theme, scalePxPerCm } = ctx;
  addDoorToLeftGeometry(K, group, openW, theme, scalePxPerCm);
});

/** v3 `_drawRightDoor`: mirror TOLEFT horizontally. */
registerFloorPlanShapePainter('door:TORIGHT', (ctx) => {
  const { K, group, widthPx: w, theme, scalePxPerCm } = ctx;
  const mirror = new K.Group({
    x: w,
    scaleX: -1,
    listening: false,
  });
  group.add(mirror);
  const inner = new K.Group({ listening: false });
  mirror.add(inner);
  addDoorToLeftGeometry(K, inner, w, theme, scalePxPerCm);
});
